import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mic, BookOpen, FileText, ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import SEOMeta, { PAGE_META } from '../components/SEOMeta';
import { HomepageSchemas } from '../components/JsonLdSchema';
import FourPillarsGrid from '../components/FourPillarsGrid';
import QuoteCarousel from '../components/QuoteCarousel';
import FeaturedArticleBlock from '../components/FeaturedArticleBlock';
import InsightsSection from '../components/InsightsSection';
import NewsletterCTA from '../components/NewsletterCTA';

const IMG_EVOLUTION = "https://images.pexels.com/photos/8068807/pexels-photo-8068807.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_TRIAD     = "https://images.pexels.com/photos/8761329/pexels-photo-8761329.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_AUTHOR    = "https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/dnctg08g_1780602847089.jpg";

const BOOK_COVER = "https://customer-assets.emergentagent.com/job_30b1d3cb-b799-4691-ba56-7e824b5598c4/artifacts/p185kuox_font%20cover%20jpg.jpg";
const TEXTURE    = "https://static.prod-images.emergentagent.com/jobs/cd729cf5-4469-4028-b6ba-a9c891131dbe/images/929018f94360badb0e54f079c2f791e3cddb0afcac9f2ec54da01659721f7c1f.png";

const NAVY  = '#071B3A';
const GOLD  = '#C99316';
const IVORY = '#FAF8F3';

/* ─── Decorative hero graphic — TCL brand mark watermark ─────────────────── */
const HeroGraphic = () => (
  <div className="w-full max-w-[300px] xl:max-w-[340px] flex items-center justify-center" aria-hidden="true">
    <img
      src="https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/c4h0snj7_Third%20culture%20Leadership%20icon.png"
      alt=""
      className="w-full h-auto object-contain select-none"
      style={{ opacity: 0.18, mixBlendMode: 'luminosity', filter: 'brightness(12) saturate(0.5)' }}
    />
  </div>
);

/* ─── Situation card ───────────────────────────────────────────────────────── */
const Situation = ({ num, text }) => (
  <div className="border-t border-[#071B3A]/10 pt-5">
    <span className="block font-serif text-2xl text-[#071B3A]/15 font-light mb-3 leading-none">{num}</span>
    <p className="font-sans text-sm text-[#071B3A]/60 font-light leading-[1.75]">{text}</p>
  </div>
);

const TCL_ICON_URL = 'https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/c4h0snj7_Third%20culture%20Leadership%20icon.png';

/* ─── Fisher-Yates shuffle — returns a new shuffled array of indices 0..n-1 ── */
const shuffleIndices = (n) => {
  const arr = Array.from({ length: n }, (_, i) => i);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = arr[i]; arr[i] = arr[j]; arr[j] = tmp;
  }
  return arr;
};

import { TCL_QUOTES as HP_QUOTES } from '../data/quotes';

const HomepageDarkQuotes = () => {
  const orderRef = useRef(null);
  const posRef   = useRef(0);
  const [quoteText, setQuoteText] = useState('');
  const [visible,   setVisible]   = useState(true);
  const timerRef  = useRef(null);

  /* Update display to the shuffled position */
  const showPos = (pos) => {
    posRef.current = pos;
    setQuoteText(HP_QUOTES[orderRef.current[pos]]);
  };

  /* Advance +1 or -1 through shuffled order; reshuffle on cycle end */
  const step = (delta) => {
    const total = HP_QUOTES.length;
    let next = posRef.current + delta;
    if (next >= total) {
      /* Reshuffle — avoid opening the new cycle with the same quote */
      const lastIdx = orderRef.current[posRef.current];
      const fresh = shuffleIndices(total);
      if (fresh[0] === lastIdx) {
        const k = 1 + Math.floor(Math.random() * (total - 1));
        const tmp = fresh[0]; fresh[0] = fresh[k]; fresh[k] = tmp;
      }
      orderRef.current = fresh;
      next = 0;
    } else if (next < 0) {
      next = total - 1;
    }
    showPos(next);
  };

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setVisible(false);
      setTimeout(() => { step(1); setVisible(true); }, 750);
    }, 120000);
  };

  useEffect(() => {
    /* Shuffle on mount; random start position for variety on each visit */
    const order = shuffleIndices(HP_QUOTES.length);
    const startPos = Math.floor(Math.random() * order.length);
    orderRef.current = order;
    showPos(startPos);
    startTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNav = (delta) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setVisible(false);
    setTimeout(() => { step(delta); setVisible(true); startTimer(); }, 450);
  };

  /* Don't render until the first shuffle is ready */
  if (!quoteText) return null;

  return (
    <section
      className="relative py-24 md:py-32 overflow-hidden"
      style={{ backgroundColor: '#071B3A' }}
      data-testid="homepage-dark-quotes"
    >
      {/* Top gold rule */}
      <div
        className="absolute top-0 left-0 right-0"
        style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(201,147,22,0.25) 30%, rgba(201,147,22,0.25) 70%, transparent)' }}
      />

      {/* TCL icon watermark — low opacity, behind content */}
      <div className="tcl-quote-watermark">
        <img
          src={TCL_ICON_URL}
          alt=""
          aria-hidden="true"
          className="w-[420px] h-[420px] md:w-[520px] md:h-[520px] object-contain select-none"
          style={{ opacity: 0.06, mixBlendMode: 'luminosity' }}
        />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-16 text-center">
        {/* Section label */}
        <span
          className="block text-[11px] tracking-[0.32em] uppercase font-sans font-medium mb-14"
          style={{ color: 'rgba(201,147,22,0.65)' }}
        >
          Leadership Across Cultures
        </span>

        {/* Quote — fades in/out on transition */}
        <div
          className={visible ? 'tcl-quote-show' : 'tcl-quote-hide'}
          data-testid="homepage-quote-content"
        >
          <blockquote
            className="font-serif font-light leading-[1.35] mb-10"
            style={{ fontSize: 'clamp(1.3rem, 2.6vw, 1.9rem)', color: 'rgba(250,248,243,0.82)' }}
          >
            &ldquo;{quoteText}&rdquo;
          </blockquote>

          <div className="flex items-center justify-center gap-5">
            <div style={{ width: '44px', height: '1px', backgroundColor: 'rgba(201,147,22,0.3)' }} />
            <cite
              className="not-italic font-sans"
              style={{ fontSize: '11px', letterSpacing: '0.22em', color: 'rgba(201,147,22,0.5)' }}
            >
              &#8212;&nbsp;Promise Monday
            </cite>
            <div style={{ width: '44px', height: '1px', backgroundColor: 'rgba(201,147,22,0.3)' }} />
          </div>
        </div>

        {/* Prev / Next — no counter, no library-size indicator */}
        <div className="flex items-center justify-center gap-8 mt-12">
          <button
            onClick={() => handleNav(-1)}
            aria-label="Previous insight"
            data-testid="quote-prev-btn"
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
            style={{ border: '1px solid rgba(201,147,22,0.22)', color: 'rgba(201,147,22,0.5)', backgroundColor: 'rgba(201,147,22,0.05)' }}
          >
            <ChevronLeft size={18} />
          </button>
          {/* Subtle decorative separator — reveals nothing about the library size */}
          <span aria-hidden="true" style={{ color: 'rgba(201,147,22,0.2)', fontSize: '7px' }}>&#9670;</span>
          <button
            onClick={() => handleNav(1)}
            aria-label="Next insight"
            data-testid="quote-next-btn"
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
            style={{ border: '1px solid rgba(201,147,22,0.22)', color: 'rgba(201,147,22,0.5)', backgroundColor: 'rgba(201,147,22,0.05)' }}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Bottom gold rule */}
      <div
        className="absolute bottom-0 left-0 right-0"
        style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(201,147,22,0.25) 30%, rgba(201,147,22,0.25) 70%, transparent)' }}
      />
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
const Home = () => {
  useEffect(() => {
    const els = document.querySelectorAll('[data-reveal]');
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.style.opacity = '1';
          e.target.style.transform = 'translateY(0)';
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.06 });
    els.forEach((el, i) => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(18px)';
      el.style.transition = `opacity 0.55s ease ${i * 0.04}s, transform 0.55s ease ${i * 0.04}s`;
      io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  return (
    <div className="bg-[#FAF8F3]" data-testid="home-page">
      <SEOMeta url="/" {...(PAGE_META?.home || {})} />
      <HomepageSchemas />

      {/* ═══ §1 HERO ════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col overflow-hidden bg-[#071B3A]">
        <div className="relative z-10 flex-1 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full flex items-center pt-28 pb-24 lg:pt-32 lg:pb-0 lg:min-h-screen lg:py-0">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center w-full">

            {/* Left: copy */}
            <div className="lg:col-span-7" data-testid="hero-text">
              <span
                className="block text-[10px] tracking-[0.32em] uppercase font-sans font-medium mb-8"
                style={{ color: `${GOLD}99` }}
                data-reveal
              >
                The Hidden Layer of Leadership
              </span>

              <h1
                className="font-serif font-light tracking-tight leading-[1.06] mb-8"
                style={{ fontSize: 'clamp(2.4rem, 4.8vw, 4.4rem)', color: IVORY }}
                data-reveal
                data-testid="hero-headline"
              >
                Your team may speak<br />the same language<br />
                <span style={{ color: `${IVORY}B0` }}>and still mean different things.</span>
              </h1>

              <p
                className="font-sans font-light leading-[1.9] mb-10 max-w-[520px]"
                style={{ fontSize: '1.0625rem', color: `${IVORY}BB` }}
                data-reveal
              >
                Culture shapes how people read confidence, respect, disagreement,
                authority, silence and trust. Third Culture Leadership helps leaders
                recognise those hidden signals, reduce costly misreading, and build
                shared ways of working across difference.
              </p>

              <div className="flex flex-wrap gap-4" data-reveal>
                <Link
                  to="/cfq-assessment"
                  data-testid="hero-cta-cfq"
                  className="inline-flex items-center gap-3 px-8 py-4 font-sans text-[11px] uppercase tracking-[0.18em] font-medium transition-all duration-300 hover:bg-gold hover:text-navy"
                  style={{ border: `1px solid ${GOLD}99`, color: GOLD }}
                >
                  Discover Your Cultural Fluency <ArrowRight size={13} />
                </Link>
                <a
                  href="#framework"
                  data-testid="hero-cta-framework"
                  className="inline-flex items-center gap-2 pb-px font-sans text-[11px] uppercase tracking-[0.18em] transition-all duration-300"
                  style={{ borderBottom: `1px solid ${IVORY}26`, color: `${IVORY}50` }}
                  onMouseEnter={e => { e.currentTarget.style.color = GOLD; e.currentTarget.style.borderBottomColor = `${GOLD}80`; }}
                  onMouseLeave={e => { e.currentTarget.style.color = `${IVORY}50`; e.currentTarget.style.borderBottomColor = `${IVORY}26`; }}
                  onClick={e => { e.preventDefault(); document.getElementById('framework')?.scrollIntoView({ behavior: 'smooth' }); }}
                >
                  Explore Third Culture Leadership <ArrowRight size={12} />
                </a>
              </div>
            </div>

            {/* Right: decorative globe */}
            <div className="lg:col-span-5 hidden lg:flex justify-center items-center" data-reveal>
              <HeroGraphic />
            </div>
          </div>
        </div>

        {/* Bottom anchor — fills the screen purposefully */}
        <div
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full pb-10 hidden lg:grid grid-cols-3 gap-6"
          style={{ borderTop: `1px solid ${IVORY}0A` }}
        >
          {[
            { num: '20', label: 'reflective questions' },
            { num: '4',  label: 'cultural fluency pillars' },
            { num: '—',  label: 'results stay on your device' },
          ].map(item => (
            <div key={item.num} className="pt-7 flex items-baseline gap-3">
              <span className="font-serif text-2xl font-light" style={{ color: `${GOLD}60` }}>{item.num}</span>
              <span className="font-sans text-xs uppercase tracking-[0.16em]" style={{ color: `${IVORY}55` }}>{item.label}</span>
            </div>
          ))}
        </div>

        {/* Bottom rule */}
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: `${GOLD}26` }} />
      </section>

      {/* ═══ §1B ROTATING INSIGHTS — dark navy dead space ══════════════════ */}
      <HomepageDarkQuotes />

      {/* ═══ §1C TRANSITION STATEMENT ═══════════════════════════════════════ */}
      <section className="py-20 md:py-24" style={{ backgroundColor: IVORY }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center" data-reveal>
          <p
            className="font-serif font-light leading-[1.35]"
            style={{ fontSize: 'clamp(1.55rem, 2.8vw, 2.4rem)', color: `${NAVY}CC` }}
          >
            The hardest cultural differences are often<br className="hidden md:block" />
            the ones nobody names.
          </p>
          <div
            className="mx-auto mt-10"
            style={{ width: '32px', height: '1px', backgroundColor: `${GOLD}60` }}
          />
        </div>
      </section>

      {/* ═══ §2 SITUATIONS — light ivory break ══════════════════════════════ */}
      <section className="py-20 md:py-28" style={{ backgroundColor: IVORY }} data-reveal>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-12 gap-12 lg:gap-20">

            {/* Left: framing text */}
            <div className="md:col-span-5">
              <span
                className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-5"
                style={{ color: `${GOLD}CC` }}
              >
                Where the difficulty lives
              </span>
              <h2
                className="font-serif font-light tracking-tight leading-[1.1] mb-6"
                style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: NAVY }}
              >
                The assumptions that go unspoken.
              </h2>
              <p
                className="font-sans font-light leading-[1.85]"
                style={{ fontSize: '0.9375rem', color: `${NAVY}CC` }}
              >
                Multicultural teams don't fail because people are difficult.
                They struggle because assumptions about how to communicate,
                how to disagree and how to show respect can stay invisible
                until they collide.
              </p>
              <p
                className="font-sans font-light leading-[1.85] mt-4"
                style={{ fontSize: '0.9375rem', color: `${NAVY}AA` }}
              >
                Third Culture Leadership gives leaders a structured way to
                surface those assumptions, clarify meaning and build shared
                team practices—without asking anyone to erase their identity.
              </p>
            </div>

            {/* Right: four situations */}
            <div className="md:col-span-7 grid sm:grid-cols-2 gap-6">
              <Situation
                num="01"
                text="A team member falls silent during a meeting. Their manager reads it as agreement. The silence meant unspoken concern."
              />
              <Situation
                num="02"
                text="Direct feedback is given as a mark of trust. It is received as a public attack. The relationship doesn't recover."
              />
              <Situation
                num="03"
                text="A decision is made and communicated. Two members assumed it needed unanimous consent. They keep re-opening it."
              />
              <Situation
                num="04"
                text="A leader opens the floor for input. The team waits for a direction. Both sides leave assuming the other wasn't engaged."
              />
            </div>
          </div>
        </div>
      </section>

      {/* ═══ §2B EVOLUTION — ivory ══════════════════════════════════════════ */}
      <section className="overflow-hidden" style={{ backgroundColor: IVORY }}>
        <div className="grid lg:grid-cols-2 min-h-[600px]">

          {/* Left: text */}
          <div className="py-24 md:py-28 px-8 sm:px-12 lg:pl-20 xl:pl-28 lg:pr-16 flex flex-col justify-center" data-reveal>

            <span
              className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-5"
              style={{ color: `${GOLD}CC` }}
            >
              The evolution
            </span>

            <h2
              className="font-serif font-light tracking-tight leading-[1.08] mb-8"
              style={{ fontSize: 'clamp(1.6rem, 2.6vw, 2.2rem)', color: NAVY }}
            >
              From Third Culture Kids<br />to Third Culture Leadership
            </h2>

            <p
              className="font-sans font-light leading-[1.9] mb-5"
              style={{ fontSize: '0.9375rem', color: `${NAVY}99` }}
            >
              The idea of a "third culture" dates back to the 1950s, when sociologists John and Ruth Hill Useem studied Americans living and working in India. They observed that when people from different cultural worlds interacted closely, they often created a shared way of relating that belonged fully to neither culture. The Useems called this emerging intercultural space the third culture.
            </p>
            <p
              className="font-sans font-light leading-[1.9] mb-5"
              style={{ fontSize: '0.9375rem', color: `${NAVY}99` }}
            >
              Ruth Hill Useem later extended the idea to children growing up across cultures, coining the term Third Culture Kid (TCK) for those whose formative lives unfolded between their parents' culture and the societies in which they were raised. The concept was later developed and popularised further by David Pollock and Ruth Van Reken.
            </p>
            <p
              className="font-sans font-light leading-[1.9] mb-5"
              style={{ fontSize: '0.9375rem', color: `${NAVY}99` }}
            >
              Third Culture Leadership builds on that lineage, but applies the underlying idea to leadership. Here, the "third culture" is not a nationality and not a compromise between two identities. It is the shared operating space that can be intentionally created when people with different cultural assumptions still need to work, decide, disagree, build trust and achieve outcomes together.
            </p>

            <div
              className="py-5 px-6 mb-7"
              style={{ backgroundColor: `${NAVY}07`, borderLeft: `3px solid ${GOLD}` }}
            >
              <p className="font-serif font-light leading-[1.65]" style={{ fontSize: '1.0625rem', color: NAVY }}>
                Third Culture Space is what the room can become.<br />
                Third Culture Leadership is how the leader helps make it so.
              </p>
            </div>

            {/* Discreet historical source note */}
            <p
              className="font-sans font-light leading-[1.7]"
              style={{ fontSize: '0.78rem', color: `${NAVY}3D`, borderTop: `1px solid ${NAVY}0D`, paddingTop: '1rem' }}
            >
              The terms "third culture" and "Third Culture Kid" originate in the cross-cultural research of John Useem, Ruth Hill Useem, David C. Pollock, and Ruth E. Van Reken. Third Culture Leadership is Promise Monday's leadership application of that underlying framework.
            </p>
          </div>

          {/* Right: image — full bleed */}
          <div className="relative h-[420px] lg:h-auto" data-reveal>
            <img
              src={IMG_EVOLUTION}
              alt="Diverse professionals in a leadership discussion"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: 'saturate(0.65)' }}
            />
            <div className="absolute inset-0" style={{ backgroundColor: `${NAVY}22` }} />
          </div>

        </div>
      </section>

      {/* ═══ §2C FEATURED INSIGHT — live article block ═══════════════════════ */}
      <FeaturedArticleBlock />

      {/* ═══ §2D INSIGHTS FOR LEADING ACROSS CULTURES ══════════════════════ */}
      <InsightsSection />

      {/* ═══ §3 FOUR PILLARS — dark navy ════════════════════════════════════ */}
      <section className="py-20 md:py-28 bg-[#071B3A]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24" data-reveal>
          <div className="mb-4">
            <span
              className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-4"
              style={{ color: `${GOLD}B3` }}
            >
              The framework
            </span>
            <div className="grid md:grid-cols-2 gap-4 md:gap-8 items-end mb-10">
              <h2
                className="font-serif font-light tracking-tight leading-[1.1]"
                style={{ fontSize: 'clamp(1.75rem, 3vw, 2.4rem)', color: IVORY }}
              >
                Four pillars for working<br />across cultural difference.
              </h2>
              <p
                className="font-sans font-light leading-[1.8] max-w-sm"
                style={{ fontSize: '0.9rem', color: `${IVORY}85` }}
              >
                The Cultural Fluency Quotient (CFQ) is built on these four pillars.
                Together they form a practical framework for any leader or team
                navigating cultural complexity.
              </p>
            </div>
          </div>
          <FourPillarsGrid />
        </div>
      </section>

      {/* ═══ §4 CFQ ASSESSMENT ═══════════════════════════════════════════════ */}
      <section className="py-20 md:py-24" style={{ backgroundColor: IVORY }} data-reveal>
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span
                className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-5"
                style={{ color: `${GOLD}B3` }}
              >
                Reflective development tool
              </span>
              <h2
                className="font-serif font-light tracking-tight leading-[1.1] mb-5"
                style={{ fontSize: 'clamp(1.75rem, 3vw, 2.4rem)', color: NAVY }}
              >
                Cultural Fluency Quotient
              </h2>
              <p
                className="font-sans font-light leading-[1.85] mb-3 max-w-md"
                style={{ fontSize: '0.9375rem', color: NAVY + '99' }}
              >
                Twenty questions across the four pillars. A reflective tool for
                individuals who want to understand how they lead and work across
                cultural difference—not a performance measure or a hiring instrument.
              </p>
              <p
                className="font-sans font-light leading-[1.8] mb-8"
                style={{ fontSize: '0.875rem', color: NAVY + '70' }}
              >
                Results are generated on your device. Responses are never stored.
              </p>
              <Link
                to="/cfq-assessment"
                data-testid="cfq-cta-main"
                className="inline-flex items-center gap-3 px-8 py-4 font-sans text-sm uppercase tracking-[0.14em] font-medium transition-all duration-300 hover:bg-[#C99316] hover:text-[#071B3A]"
                style={{ border: '1px solid rgba(201,147,22,0.6)', color: GOLD }}
              >
                Begin Assessment <ArrowRight size={15} />
              </Link>
            </div>

            {/* Compact pillar grid */}
            <div data-testid="cfq-pillar-grid">
              <FourPillarsGrid compact />
            </div>
          </div>
        </div>
      </section>

      {/* ═══ §4B TRIAD — image left / text right ════════════════════════════ */}
      <section className="overflow-hidden" style={{ backgroundColor: IVORY }}>
        <div className="grid lg:grid-cols-2 min-h-[560px]">

          {/* Left: image — full bleed */}
          <div className="relative h-[360px] lg:h-auto order-last lg:order-first" data-reveal>
            <img
              src={IMG_TRIAD}
              alt="Leadership workshop with diverse professional audience"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: 'saturate(0.65)' }}
            />
            <div className="absolute inset-0" style={{ backgroundColor: NAVY + '30' }} />
          </div>

          {/* Right: triad content */}
          <div className="py-24 md:py-28 px-8 sm:px-12 lg:px-16 xl:px-20 flex flex-col justify-center" data-reveal>
            <span
              className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-5"
              style={{ color: GOLD + 'B3' }}
            >
              The operating system
            </span>
            <h2
              className="font-serif font-light tracking-tight leading-[1.08] mb-7"
              style={{ fontSize: 'clamp(1.75rem, 3vw, 2.3rem)', color: NAVY }}
            >
              Multicultural leadership requires more than goodwill.
            </h2>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-4 py-4 px-5" style={{ border: '1px solid rgba(7,27,58,0.1)', backgroundColor: 'rgba(7,27,58,0.025)' }}>
                <span className="font-serif text-lg font-light flex-shrink-0 mt-0.5" style={{ color: GOLD + '80', minWidth: '2rem' }}>IQ</span>
                <div>
                  <span className="text-[10px] tracking-[0.18em] uppercase font-sans block mb-1" style={{ color: NAVY + '70' }}>Cognitive Intelligence</span>
                  <p className="font-sans font-light leading-[1.75]" style={{ fontSize: '0.875rem', color: NAVY + '88' }}>helps you solve problems.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 py-4 px-5" style={{ border: '1px solid rgba(7,27,58,0.1)', backgroundColor: 'rgba(7,27,58,0.025)' }}>
                <span className="font-serif text-lg font-light flex-shrink-0 mt-0.5" style={{ color: GOLD + '80', minWidth: '2rem' }}>EQ</span>
                <div>
                  <span className="text-[10px] tracking-[0.18em] uppercase font-sans block mb-1" style={{ color: NAVY + '70' }}>Emotional Intelligence</span>
                  <p className="font-sans font-light leading-[1.75]" style={{ fontSize: '0.875rem', color: NAVY + '88' }}>helps you manage emotion and relationships.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 py-4 px-5" style={{ border: '1px solid rgba(201,147,22,0.3)', backgroundColor: 'rgba(201,147,22,0.05)' }}>
                <span className="font-serif text-lg font-light flex-shrink-0 mt-0.5" style={{ color: GOLD, minWidth: '2rem' }}>CQ</span>
                <div>
                  <span className="text-[10px] tracking-[0.18em] uppercase font-sans block mb-1" style={{ color: GOLD + '99' }}>Cultural Intelligence</span>
                  <p className="font-sans font-light leading-[1.75]" style={{ fontSize: '0.875rem', color: NAVY + '80' }}>helps you interpret and adapt across cultures so trust can form, meaning can travel accurately, and outcomes land as intended.</p>
                </div>
              </div>
            </div>

            <p
              className="font-sans font-light leading-[1.85]"
              style={{ fontSize: '0.875rem', color: NAVY + '80' }}
            >
              When CQ is absent, leaders do not simply make mistakes. They misread people. They mislabel competence. They mistake silence for disengagement. They confuse deference with lack of initiative.
            </p>
          </div>

        </div>
      </section>

      {/* ═══ §4C FACILITATOR CTA ═════════════════════════════════════════════ */}
      <section className="py-20 md:py-28" style={{ backgroundColor: IVORY }} data-reveal>
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            {/* Left: copy */}
            <div>
              <span className="block text-[11px] tracking-[0.28em] uppercase font-medium mb-4" style={{ color: 'rgba(201,147,22,0.7)' }}>
                For Event Facilitators
              </span>
              <h2
                className="font-serif font-light tracking-tight leading-[1.1] mb-5"
                style={{ fontSize: 'clamp(1.6rem, 2.8vw, 2.5rem)', color: NAVY }}
              >
                Bring the CFQ<br />into your live event.
              </h2>
              <p className="font-sans font-light leading-[1.8] mb-8" style={{ fontSize: '0.9375rem', color: 'rgba(7,27,58,0.7)' }}>
                Request a session code, share a single URL or QR code with your audience, and watch their results appear on screen in real time. No app, no participant sign-up.
              </p>
              <Link
                to="/facilitate/login"
                className="inline-flex items-center gap-2.5 px-7 py-3.5 font-sans text-sm uppercase tracking-[0.12em] font-medium transition-all duration-300"
                style={{ border: '1px solid #071B3A', color: NAVY, backgroundColor: 'transparent' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = NAVY; e.currentTarget.style.color = IVORY; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = NAVY; }}
                data-testid="home-facilitator-portal-link"
              >
                Facilitator Portal <ArrowRight size={15} />
              </Link>
            </div>
            {/* Right: 3 steps */}
            <div className="flex flex-col gap-6">
              {[
                { n: '1', title: 'Request your session code', desc: 'Submit your event name via the Facilitator Portal. Approved within 24 hours.' },
                { n: '2', title: 'Share one link with your audience', desc: 'Participants visit a URL or scan a QR code — no account or download required.' },
                { n: '3', title: 'See band results live', desc: "Watch each participant's CFQ results arrive on your screen as they finish." },
              ].map(({ n, title, desc }) => (
                <div key={n} className="flex gap-4 items-start">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-mono shrink-0"
                    style={{ backgroundColor: 'rgba(7,27,58,0.06)', color: NAVY, border: '1px solid rgba(7,27,58,0.12)' }}
                  >
                    {n}
                  </div>
                  <div>
                    <p className="font-sans text-sm font-medium mb-1" style={{ color: 'rgba(7,27,58,0.8)' }}>{title}</p>
                    <p className="font-sans text-[13px] font-light leading-relaxed" style={{ color: 'rgba(7,27,58,0.6)' }}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ §4D MATRIX TEASER — compact callout ════════════════════════════ */}
      <section className="py-14 md:py-16" style={{ backgroundColor: NAVY }}>
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">

            {/* Left: headline + CTA */}
            <div data-reveal>
              <span
                className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-4"
                style={{ color: GOLD + 'B3' }}
              >
                Reflective Framework
              </span>
              <h2
                className="font-serif font-light tracking-tight leading-[1.08] mb-5"
                style={{ fontSize: 'clamp(1.55rem, 2.5vw, 2.1rem)', color: IVORY }}
              >
                Where do you currently<br />lead from?
              </h2>
              <p
                className="font-sans font-light leading-[1.85] mb-7"
                style={{ fontSize: '0.9rem', color: 'rgba(250,248,243,0.78)' }}
              >
                The Cultural Fluency Matrix maps how EQ and CQ interact across four
                distinct leadership profiles.
              </p>
              <Link
                to="/cfq-matrix"
                className="inline-flex items-center gap-2.5 font-sans text-[11px] uppercase tracking-[0.18em] font-medium transition-all duration-300 hover:gap-4"
                style={{ color: GOLD }}
                data-testid="home-matrix-cta"
              >
                Explore the Matrix <ArrowRight size={14} />
              </Link>
            </div>

            {/* Right: Official Cultural Fluency Matrix image */}
            <div className="flex items-start justify-center" data-reveal>
              <img
                src="https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/k0n4tp7m_Matrix.png"
                alt="Promise Monday Cultural Fluency Matrix™"
                className="w-full max-w-md rounded-2xl"
                style={{
                  boxShadow: '0 24px 64px rgba(0,0,0,0.45), 0 0 0 1px rgba(201,147,22,0.14)',
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ═══ §5 PATHWAYS — ivory break ═══════════════════════════════════════ */}
      <section className="py-20 md:py-28" style={{ backgroundColor: '#EDE8DC' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 gap-0 md:divide-x divide-[#071B3A]/10">

            {/* Individuals */}
            <div className="pb-12 md:pb-0 md:pr-12 lg:pr-20" data-reveal>
              <span
                className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-6"
                style={{ color: `${GOLD}CC` }}
              >
                For Individuals
              </span>
              <h3
                className="font-serif font-light tracking-tight leading-[1.1] mb-5"
                style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', color: NAVY }}
              >
                Develop your ability to work<br className="hidden md:block" /> across cultures.
              </h3>
              <p
                className="font-sans font-light leading-[1.85] mb-3 max-w-md"
                style={{ fontSize: '0.9375rem', color: `${NAVY}80` }}
              >
                Whether you manage across borders, collaborate in diverse teams
                or simply want to communicate more effectively with people from
                different backgrounds—the CFQ gives you a clear starting point.
              </p>
              <p
                className="font-sans font-light leading-[1.8] mb-8"
                style={{ fontSize: '0.875rem', color: `${NAVY}59` }}
              >
                Your results are private. Development goals are your own.
              </p>
              <Link
                to="/cfq-assessment"
                data-testid="path-individuals"
                className="inline-flex items-center gap-2 font-sans text-sm uppercase tracking-[0.14em] font-medium transition-all duration-300"
                style={{ color: `${NAVY}B3` }}
                onMouseEnter={e => { e.currentTarget.style.color = NAVY; }}
                onMouseLeave={e => { e.currentTarget.style.color = `${NAVY}B3`; }}
              >
                Take the CFQ <ArrowRight size={13} />
              </Link>
            </div>

            {/* Organisations */}
            <div className="pt-12 md:pt-0 md:pl-12 lg:pl-20" data-reveal>
              <span
                className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-6"
                style={{ color: `${GOLD}CC` }}
              >
                For Organisations
              </span>
              <h3
                className="font-serif font-light tracking-tight leading-[1.1] mb-5"
                style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', color: NAVY }}
              >
                Build trust and effective<br className="hidden md:block" /> collaboration in your team.
              </h3>
              <p
                className="font-sans font-light leading-[1.85] mb-3 max-w-md"
                style={{ fontSize: '0.9375rem', color: `${NAVY}80` }}
              >
                Facilitated workshops use the CFQ framework to give multicultural
                teams a shared language for navigating difference. Each participant
                takes the assessment with full informed consent. Sharing is always
                voluntary.
              </p>
              <p
                className="font-sans font-light leading-[1.8] mb-8"
                style={{ fontSize: '0.875rem', color: `${NAVY}59` }}
              >
                Sessions focus on awareness and practical next steps—not labelling.
              </p>
              <Link
                to="/organisations"
                data-testid="path-organisations"
                className="inline-flex items-center gap-2 font-sans text-sm uppercase tracking-[0.14em] font-medium transition-all duration-300"
                style={{ color: `${NAVY}B3` }}
                onMouseEnter={e => { e.currentTarget.style.color = NAVY; }}
                onMouseLeave={e => { e.currentTarget.style.color = `${NAVY}B3`; }}
              >
                Enquire about workshops <ArrowRight size={13} />
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* ═══ §5B OUTCOMES — ivory ════════════════════════════════════════════ */}
      <section className="py-20 md:py-28" style={{ backgroundColor: IVORY }} data-reveal>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-12">
            <span
              className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-5"
              style={{ color: GOLD + 'B3' }}
            >
              What it produces
            </span>
            <h2
              className="font-serif font-light tracking-tight leading-[1.08]"
              style={{ fontSize: 'clamp(1.75rem, 3vw, 2.4rem)', color: NAVY }}
            >
              Trust. Dignity. Alignment. Performance.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px" style={{ backgroundColor: 'rgba(7,27,58,0.07)' }}>
            {[
              {
                word: 'Trust',
                text: 'Without it, people withdraw. Compliance replaces commitment. Teams become polite but fragile.',
              },
              {
                word: 'Dignity',
                text: 'Once dignity is ranked by power, every decision becomes contaminated by resentment or fear.',
              },
              {
                word: 'Alignment',
                text: 'Unity cannot be forced by sameness. It must be built through shared expectations and shared interpretation.',
              },
              {
                word: 'Performance',
                text: 'Leadership must still deliver. The point is not to avoid hard decisions, but to make them without damaging people.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="p-8 md:p-10 flex flex-col"
                style={{ backgroundColor: IVORY }}
              >
                <span
                  className="font-serif text-3xl font-light mb-5 block"
                  style={{ color: GOLD, opacity: 0.8 }}
                >
                  {item.word}
                </span>
                <p
                  className="font-sans font-light leading-[1.85]"
                  style={{ fontSize: '0.9rem', color: NAVY + '88' }}
                >
                  {item.text}
                </p>
              </div>
            ))}
          </div>

          <p
            className="mt-10 font-sans font-light leading-[1.75] max-w-xl"
            style={{ fontSize: '0.875rem', color: NAVY + '70' }}
          >
            These outcomes remain constant across sectors — whether you lead a hospital unit, a multinational organisation, a diplomatic mission, or a community initiative.
          </p>
        </div>
      </section>

      {/* ═══ §6 WHAT WE OFFER ═══════════════════════════════════════════════ */}
      <section className="py-20 md:py-24" style={{ backgroundColor: '#EDE8DC' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span
            className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-12"
            style={{ color: GOLD + 'B3' }}
            data-reveal
          >
            What we offer
          </span>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                Icon: Mic,
                title: 'Speaking & Workshops',
                desc: 'Keynotes and workshops for leadership teams, HR professionals and organisations navigating cultural complexity.',
                link: '/speaking',
                label: 'See speaking topics',
              },
              {
                Icon: BookOpen,
                title: 'Books & Courses',
                desc: 'Self-paced modules on cultural fluency, identity and leading across difference — plus published works by Dr Promise Monday.',
                link: '/courses',
                label: 'Explore modules',
              },
              {
                Icon: FileText,
                title: 'Articles & Insights',
                desc: 'Practical writing on leadership, communication and the realities of multicultural team life.',
                link: '/articles',
                label: 'Read articles',
              },
            ].map((item, i) => (
              <Link
                key={i}
                to={item.link}
                className="group p-8 md:p-10 transition-all duration-300 bg-white"
                style={{ border: '1px solid rgba(7,27,58,0.08)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(201,147,22,0.35)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(7,27,58,0.08)'; }}
                data-reveal
              >
                <item.Icon
                  size={20}
                  className="mb-7 transition-colors duration-300"
                  style={{ color: GOLD + '80' }}
                />
                <h3
                  className="font-serif text-xl font-light tracking-tight mb-3"
                  style={{ color: NAVY }}
                >
                  {item.title}
                </h3>
                <p
                  className="font-sans font-light leading-[1.8] mb-6"
                  style={{ fontSize: '0.9rem', color: NAVY + '88' }}
                >
                  {item.desc}
                </p>
                <span
                  className="text-xs tracking-[0.14em] uppercase font-sans transition-colors duration-300"
                  style={{ color: GOLD + '80' }}
                >
                  {item.label} <ArrowUpRight size={12} className="inline" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ §6B SELF-PACED MODULES ═══════════════════════════════════════ */}
      <section className="py-20 md:py-28" style={{ backgroundColor: IVORY }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">

          {/* Section header */}
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-7">
              <span
                className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-5"
                style={{ color: `${GOLD}CC` }}
              >
                Self-Paced Learning
              </span>
              <h2
                className="font-serif font-light tracking-tight leading-[1.08] mb-5"
                style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: NAVY }}
              >
                Cultural Fluency Mini-Course
              </h2>
              <p
                className="font-sans font-light leading-[1.85] max-w-lg"
                style={{ fontSize: '0.9375rem', color: `${NAVY}73` }}
              >
                Four structured modules on the pillars of Third Culture Leadership.
                Module 1 is free. Modules 2–4 are available individually or as a bundle.
              </p>
            </div>
            <div className="md:col-span-5 flex items-end justify-start md:justify-end">
              <Link
                to="/courses"
                className="inline-flex items-center gap-2 font-sans text-sm uppercase tracking-[0.14em] font-medium transition-all duration-300 pb-px"
                style={{ color: NAVY, borderBottom: `1px solid ${NAVY}26` }}
                onMouseEnter={e => { e.currentTarget.style.color = GOLD; e.currentTarget.style.borderBottomColor = `${GOLD}80`; }}
                onMouseLeave={e => { e.currentTarget.style.color = NAVY; e.currentTarget.style.borderBottomColor = `${NAVY}26`; }}
                data-testid="view-courses-link"
              >
                View full course <ArrowRight size={13} />
              </Link>
            </div>
          </div>

          {/* Module cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0" style={{ borderTop: `1px solid ${NAVY}0F` }}>
            {[
              {
                num: '01',
                title: 'Self-Anchoring',
                desc: 'Establishing your cultural identity as a stable leadership foundation.',
                tag: 'Free',
                tagStyle: { color: '#16a34a', borderColor: 'rgba(22,163,74,0.2)', backgroundColor: 'rgba(22,163,74,0.05)' },
                link: '/courses',
                cta: 'Start Free',
              },
              {
                num: '02',
                title: 'Perspective-Shifting',
                desc: 'Developing the ability to read cultural situations through multiple lenses.',
                tag: '£10',
                tagStyle: { color: GOLD, borderColor: `${GOLD}33`, backgroundColor: `${GOLD}08` },
                link: 'https://buy.stripe.com/cNi7sK71n0eDg3HahJ8so05',
                cta: 'Unlock',
                external: true,
              },
              {
                num: '03',
                title: 'Adaptive Communication',
                desc: 'Expressing clearly across cultures without losing your voice.',
                tag: '£10',
                tagStyle: { color: GOLD, borderColor: `${GOLD}33`, backgroundColor: `${GOLD}08` },
                link: 'https://buy.stripe.com/fZueVcadz1iH7xb9dF8so03',
                cta: 'Unlock',
                external: true,
              },
              {
                num: '04',
                title: 'Relational Integration',
                desc: 'Building trust, influence, and cohesion across cultural boundaries.',
                tag: '£10',
                tagStyle: { color: GOLD, borderColor: `${GOLD}33`, backgroundColor: `${GOLD}08` },
                link: 'https://buy.stripe.com/dRm8wO5Xje5tdVz0H98so04',
                cta: 'Unlock',
                external: true,
              },
            ].map((mod, i) => (
              <div
                key={i}
                className="p-8 flex flex-col"
                style={{ borderBottom: `1px solid ${NAVY}0F`, borderRight: i < 3 ? `1px solid ${NAVY}0F` : 'none' }}
                data-testid={`homepage-module-${mod.num}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <span
                    className="font-serif text-3xl font-light leading-none"
                    style={{ color: `${NAVY}1A` }}
                  >
                    {mod.num}
                  </span>
                  <span
                    className="text-[10px] uppercase tracking-[0.15em] font-medium px-2.5 py-1 border"
                    style={mod.tagStyle}
                  >
                    {mod.tag}
                  </span>
                </div>
                <h3
                  className="font-serif font-light tracking-tight mb-3"
                  style={{ fontSize: '1.125rem', color: NAVY }}
                >
                  {mod.title}
                </h3>
                <p
                  className="font-sans font-light leading-[1.8] mb-8 flex-1"
                  style={{ fontSize: '0.875rem', color: `${NAVY}66` }}
                >
                  {mod.desc}
                </p>
                {mod.external ? (
                  <a
                    href={mod.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.15em] font-medium transition-colors duration-300"
                    style={{ color: GOLD }}
                    onMouseEnter={e => { e.currentTarget.style.color = NAVY; }}
                    onMouseLeave={e => { e.currentTarget.style.color = GOLD; }}
                    data-testid={`unlock-module-${mod.num}`}
                  >
                    {mod.cta} <ArrowRight size={12} />
                  </a>
                ) : (
                  <Link
                    to={mod.link}
                    className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.15em] font-medium transition-colors duration-300"
                    style={{ color: NAVY }}
                    onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
                    onMouseLeave={e => { e.currentTarget.style.color = NAVY; }}
                    data-testid={`start-module-${mod.num}`}
                  >
                    {mod.cta} <ArrowRight size={12} />
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Bundle strip */}
          <div
            className="mt-0 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 px-8 py-6"
            style={{ borderLeft: `1px solid ${NAVY}0F`, borderRight: `1px solid ${NAVY}0F`, borderBottom: `1px solid ${NAVY}0F` }}
          >
            <div>
              <p
                className="font-sans text-xs uppercase tracking-[0.2em] font-medium mb-1"
                style={{ color: `${GOLD}CC` }}
              >
                Best Value
              </p>
              <p className="font-serif font-light" style={{ fontSize: '1rem', color: NAVY }}>
                All three paid modules — save £10
              </p>
              <p
                className="font-sans font-light"
                style={{ fontSize: '0.8125rem', color: `${NAVY}59` }}
              >
                Modules 2, 3 &amp; 4 together &bull; <span style={{ textDecoration: 'line-through', opacity: 0.45 }}>£30</span> <strong style={{ color: GOLD }}>£20</strong>
              </p>
            </div>
            <a
              href="https://buy.stripe.com/cNieVc85r7H5dVz3Tl8so07"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3 font-sans text-xs uppercase tracking-[0.14em] font-medium transition-all duration-300 hover:text-navy flex-shrink-0"
              style={{ border: `1px solid ${GOLD}`, color: GOLD }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = GOLD; e.currentTarget.style.color = NAVY; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = GOLD; }}
              data-testid="homepage-bundle-btn"
            >
              Get the Bundle — £20 <ArrowRight size={13} />
            </a>
          </div>

        </div>
      </section>

      {/* ═══ §7 BOOK SPOTLIGHT ═══════════════════════════════════════════════ */}
      <section className="py-24 md:py-28 relative overflow-hidden" style={{ backgroundColor: '#0D2545' }}>
        {/* Texture overlay */}
        <img
          src={TEXTURE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={{ opacity: 0.05, mixBlendMode: 'luminosity' }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-12 gap-12 lg:gap-20 items-center">

            <div className="md:col-span-5 flex justify-center" data-reveal>
              <img
                src={BOOK_COVER}
                alt="Cultural Fluency Without Compromise"
                className="w-[200px] sm:w-[240px]"
                style={{ boxShadow: '0 24px 60px rgba(0,0,0,0.55)' }}
                data-testid="book-cover-image"
              />
            </div>

            <div className="md:col-span-7" data-reveal>
              <span
                className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-5"
                style={{ color: `${GOLD}B3` }}
              >
                Published work
              </span>
              <h2
                className="font-serif font-light tracking-tight leading-[1.1] mb-3"
                style={{ fontSize: 'clamp(1.75rem, 3vw, 2.4rem)', color: IVORY }}
              >
                Cultural Fluency<br />Without Compromise
              </h2>
              <p
                className="font-sans text-sm mb-6"
                style={{ color: `${IVORY}33` }}
              >
                Dr Promise Monday &bull; Promond Publishing &bull; 2025
              </p>
              <p
                className="font-sans font-light leading-[1.85] mb-10 max-w-md"
                style={{ fontSize: '0.9375rem', color: `${IVORY}59` }}
              >
                A framework for thriving across cultures without losing your
                identity. Introduces the "third culture" as a shared space—applied
                to leadership, teams and professional life.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/books"
                  data-testid="book-cta-primary"
                  className="inline-flex items-center gap-2.5 px-7 py-3.5 font-sans text-sm uppercase tracking-[0.12em] font-medium transition-all duration-300 hover:bg-gold hover:text-navy"
                  style={{ border: `1px solid ${GOLD}`, color: GOLD }}
                >
                  Order Now <ArrowRight size={15} />
                </Link>
                <Link
                  to="/books/cultural-fluency-without-compromise"
                  className="inline-flex items-center pb-px font-sans text-sm uppercase tracking-[0.12em] transition-all duration-300"
                  style={{ borderBottom: `1px solid ${IVORY}26`, color: `${IVORY}59` }}
                  onMouseEnter={e => { e.currentTarget.style.color = GOLD; e.currentTarget.style.borderBottomColor = `${GOLD}80`; }}
                  onMouseLeave={e => { e.currentTarget.style.color = `${IVORY}59`; e.currentTarget.style.borderBottomColor = `${IVORY}26`; }}
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ §7B DOCTRINE — full bleed manifesto ════════════════════════════ */}
      <section className="overflow-hidden bg-[#071B3A]">
        <div className="grid lg:grid-cols-12 min-h-[520px]">

          {/* Author image col — 4 cols */}
          <div className="lg:col-span-4 relative h-[320px] lg:h-auto" data-reveal>
            <img
              src={IMG_AUTHOR}
              alt="Dr Promise Monday"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to right, transparent 60%, #071B3A 100%)' }}
            />
            {/* Name badge */}
            <div className="absolute bottom-6 left-6 lg:bottom-8 lg:left-8">
              <p className="font-serif font-light text-sm" style={{ color: `${IVORY}73` }}>Dr Promise Monday</p>
              <p
                className="text-[10px] tracking-[0.2em] uppercase font-sans mt-1"
                style={{ color: `${GOLD}80` }}
              >
                Paediatrician &#8226; Author &#8226; Founder, Third Culture Leadership
              </p>
            </div>
          </div>

          {/* Doctrine col — 8 cols */}
          <div
            className="lg:col-span-8 py-20 md:py-24 px-8 sm:px-12 lg:px-16 xl:px-20 flex flex-col justify-center"
            style={{ borderLeft: `1px solid ${IVORY}08` }}
            data-reveal
          >
            <span
              className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-8"
              style={{ color: `${GOLD}80` }}
            >
              The doctrine
            </span>

            <blockquote
              className="font-serif font-light leading-[1.2] mb-8"
              style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.8rem)', color: IVORY }}
            >
              Third Culture Operating Language runs on kindness.
            </blockquote>

            <div className="space-y-3 mb-10" style={{ borderLeft: `2px solid ${GOLD}33`, paddingLeft: '1.25rem' }}>
              {[
                'It is truth clothed in respect.',
                'It is clarity without domination.',
                'It is accountability without humiliation.',
                'It is the deliberate refusal to treat difference as a reason to degrade.',
              ].map((line, i) => (
                <p
                  key={i}
                  className="font-sans font-light leading-[1.65]"
                  style={{ fontSize: '1rem', color: `${IVORY}59` }}
                >
                  {line}
                </p>
              ))}
            </div>

            <p
              className="font-sans font-light leading-[1.75] max-w-lg"
              style={{ fontSize: '0.875rem', color: `${IVORY}33` }}
            >
              Kindness is not softness. It is not indecision. It is not evasion. Third Culture Space becomes possible when leaders practise that discipline consistently.
            </p>
          </div>

        </div>
      </section>

      {/* ═══ §8 QUOTE CAROUSEL ════════════════════════════════════════════════ */}
      <QuoteCarousel />

      {/* ═══ §8B NEWSLETTER ═══════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20" style={{ backgroundColor: IVORY }} data-reveal>
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <NewsletterCTA variant="light" source="homepage" />
        </div>
      </section>

      {/* ═══ §9 CLOSE CTA ════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-28 bg-[#071B3A]" data-reveal>
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <h2
            className="font-serif font-light tracking-tight leading-[1.1] mb-6"
            style={{ fontSize: 'clamp(1.75rem, 3vw, 2.6rem)', color: IVORY }}
          >
            Ready to lead<br />across cultures?
          </h2>
          <p
            className="font-sans font-light leading-[1.8] mb-10 max-w-md mx-auto"
            style={{ fontSize: '0.9375rem', color: IVORY + '4D' }}
          >
            Whether you are developing your own cultural fluency or building
            shared practices within your team, there is a clear next step.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/cfq-assessment"
              className="inline-flex items-center gap-2.5 px-7 py-3.5 font-sans text-sm uppercase tracking-[0.12em] font-medium transition-all duration-300 hover:bg-[#C99316] hover:text-[#071B3A]"
              style={{ border: '1px solid #C99316', color: GOLD }}
            >
              Take the Assessment <ArrowRight size={15} />
            </Link>
            <Link
              to="/speaking"
              className="inline-flex items-center pb-px font-sans text-sm uppercase tracking-[0.12em] transition-all duration-300"
              style={{ borderBottom: '1px solid rgba(250,248,243,0.25)', color: IVORY + '59' }}
              onMouseEnter={e => { e.currentTarget.style.color = GOLD; e.currentTarget.style.borderBottomColor = GOLD + '80'; }}
              onMouseLeave={e => { e.currentTarget.style.color = IVORY + '59'; e.currentTarget.style.borderBottomColor = 'rgba(250,248,243,0.25)'; }}
            >
              Book a Speaker
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
